// MenuOpen.tsx
import React, { FunctionComponent, useEffect, useRef } from "react";
import styled from "styled-components";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import MenuList from "@mui/material/MenuList";
import MenuItem from "@mui/material/MenuItem";
import Grow from "@mui/material/Grow";
import Colors from "../global/Colors";
import Fonts from "../global/Fonts";
import Icon from "../global/Icon";
import Tooltip from "./Tooltip";

const StyledPaper = styled(Paper)<{ $color?: 'white' | 'black', $minWidth: number, $maxWidth: string }>`
    border-radius: 4px;
    border: 1px solid ${props => props.$color === 'black' ? Colors.grey8 : Colors.grey1};
    box-shadow: 0 2px 4px ${props => props.$color === 'black' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
    min-width: ${props => props.$minWidth}px;
    max-width: ${props => props.$maxWidth || '100%'};
    max-height: 80vh;
    background-color: ${props => props.$color === 'black' ? Colors.grey9 : Colors.white};
    display: flex;
    flex-direction: column;
    // margin-left: 10px;
    // margin-right: 10px;
`;

const StyledMenuList = styled(MenuList)<{ $color?: 'white' | 'black' }>`
    display: flex;
    flex-direction: column;
    padding: 4px 0;
    gap: 2px;
    overflow-y: auto;
    // max-height: calc(80vh - 16px);
    max-height: 100%;
    
    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: ${props => props.$color === 'black' ? Colors.grey7 + ' ' + Colors.grey9 : Colors.grey3 + ' ' + Colors.white};
    
    /* Webkit scrollbar (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: ${props => props.$color === 'black' ? Colors.grey9 : Colors.white};
    }
    &::-webkit-scrollbar-thumb {
      background: ${props => props.$color === 'black' ? Colors.grey7 : Colors.grey3};
      border-radius: 4px;
    }
`;

const StyledMenuItem = styled(MenuItem)<{ $color: 'white' | 'black'; $interactive: boolean; $disabled: boolean; $error: boolean; $hasChildren: boolean; $isSelected: boolean; }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: auto;
    min-height: auto;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 14px;
    padding: 5px 12px;
    gap: 4px;
    color: ${props => 
      props.$disabled && props.$color === 'black' ? Colors.grey6 
      : props.$disabled && props.$color === 'white' ? Colors.grey5
      : props.$color === 'black' ? Colors.grey2 
      : props.$color === 'white' ? Colors.grey8 
      : Colors.grey5};
    cursor: ${props => props.$interactive && !props.$disabled ? 'pointer' : 'default'};
    opacity: ${props => props.$disabled ? 0.5 : 1};
    
    background-color: ${props => props.$isSelected ? (props.$color === 'black' ? Colors.black : Colors.grey2) : 'transparent'};

    &&:hover {
      background-color: ${props => props.$disabled ? 'transparent' : (props.$interactive ? (props.$color === 'black' ? Colors.grey8 : Colors.grey1)+80 : 'transparent')};
    }

    &&:active {
      background-color: ${props => props.$disabled ? 'transparent' : (props.$interactive ? (props.$color === 'black' ? Colors.black : Colors.grey1) : 'transparent')};
    }

    @media (pointer: coarse) {
      padding-top: 5px;
      padding-bottom: 5px;
    }
`;

const MenuItemContent = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    height: auto;
    margin: 0;
    gap: 12px;
`;

const Together = styled.div<{ $disabled?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 6px;
    min-width: 0;
`;

const TogetherRight = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px; /* Add gap between elements in TogetherRight */
    flex-shrink: 0; /* Prevent shrinking */
`;

const ChildrenContainer = styled.div<{ $color?: 'white' | 'black' }>`
    display: flex;
    width: 100%;
    justify-content: space-between;
    gap: 12px;
    align-items: center;
    min-width: 0;
    overflow: hidden;
`;

const SubText = styled.span<{ $color: 'white' | 'black'; $disabled: boolean; $error: boolean }>`
    flex: 0 1 auto;
    color: ${props => props.$color === 'black' ? Colors.grey2 : Colors.grey8};
    opacity: ${props => props.$disabled ? 0.6 : 1};
    font-weight: ${Fonts.lexendLight.fontWeight};
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
`;

const TextRight = styled.span<{ $color?: 'white' | 'black' }>`
    height: 100%;
    color: ${props => props.$color === 'black' ? Colors.grey2 : Colors.grey8};
    font-weight: ${Fonts.lexendSemiLight.fontWeight};
    font-size: 12px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: 40px;
    border: 1px solid ${props => props.$color === 'black' ? Colors.grey7 : Colors.grey3};
    padding: 2px 10px;
    max-width: 120px;
    flex: 0 0 auto;
`;

export interface MenuOption {
  text: string;
  text_sub?: string;
  text_right?: string;
  icon?: string;
  icon_left?: string;
  onClick?: () => void;
  onHover?: () => void;
  onMouseLeave?: () => void;
  disabled?: boolean;
  error?: boolean;
  below?: React.ReactNode;
  isSelected?: boolean;
  rightChildren?: React.ReactNode;
  tooltip?: Omit<React.ComponentProps<typeof Tooltip>, 'children'>;
}

export interface MenuOpenProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  options?: MenuOption[];
  children?: React.ReactNode;
  onClose?: () => void;
  color?: 'white' | 'black';
  minWidth?: number;
  maxWidth?: string;
  placement?: 'bottom-end' | 'bottom-start' | 'bottom' | 'top-end' | 'top-start' | 'top' | 'right-end' | 'right-start' | 'right' | 'left-end' | 'left-start' | 'left';
  onMenuLeave?: () => void;
}

const MenuOpen: FunctionComponent<MenuOpenProps> = ({
  anchorEl,
  open,
  onClose,
  options,
  children,
  color = 'white',
  minWidth = 180,
  maxWidth,
  placement,
  onMenuLeave,
}) => {
  const paperRef = useRef<HTMLDivElement>(null);

  const handleClose = (event: Event | React.SyntheticEvent) => {
    // Don't close if clicking on the anchor element itself
    if (
      anchorEl &&
      (anchorEl as HTMLElement).contains(event.target as HTMLElement)
    ) {
      return;
    }
    // Call onClose to close the menu
    if (onClose) onClose();
  };

  useEffect(() => {
    if (!open) return;

    const isClickInsideMuiMenu = (target: EventTarget) => {
        // Check if the target or any of its ancestors has a role='presentation'
        // which is often the immediate container for MUI Menus/Popovers (like the Select dropdown).
        const element = target as HTMLElement;
        return (
            element.closest('[role="presentation"]') !== null || 
            element.closest('.MuiMenu-root') !== null
        );
    };

    const handleMouseDown = (event: MouseEvent) => {
      // Check if the click is outside the menu AND not on the anchor element
      // AND not inside a separate MUI Menu/Popover (like the dropdown)
      if (
        paperRef.current && 
        !paperRef.current.contains(event.target as Node) &&
        !isClickInsideMuiMenu(event.target) &&
        anchorEl && 
        !(anchorEl as HTMLElement).contains(event.target as Node)
      ) {
        // Close the menu
        if (onClose) onClose();
      }
    };

    const handleTouchStart = (event: TouchEvent) => {
      // Check if the touch is outside the menu AND not on the anchor element
      // AND not inside a separate MUI Menu/Popover (like the dropdown)
      if (
        paperRef.current && 
        !paperRef.current.contains(event.target as Node) && 
        !isClickInsideMuiMenu(event.target) &&
        anchorEl && 
        !(anchorEl as HTMLElement).contains(event.target as Node)
      ) {
        // Close the menu
        if (onClose) onClose();
      }
    };

    // Add event listeners with capture phase to ensure they're called before any stopPropagation
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('touchstart', handleTouchStart, true);

    // Clean up event listeners
    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('touchstart', handleTouchStart, true);
    };
  }, [open, onClose, anchorEl]);

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      transition
      style={{ zIndex: 1500 }} // Ensure menu appears above Drawer (1200) and other layers
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [-5, 5], // [horizontal, vertical] offset in pixels
          },
        },
      ]}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement.includes('bottom') ? 'center top' : 
              placement.includes('top') ? 'center bottom' : 
              placement.includes('left') ? 'right center' : 'left center',
          }}
        >
          <StyledPaper id='paper' ref={paperRef} $color={color} $minWidth={minWidth} $maxWidth={maxWidth}>
              {children}
              {options && (
            <StyledMenuList 
                $color={color} 
                autoFocusItem={open} 
                onMouseLeave={onMenuLeave}
            >
              {options?.map((option, index) => {
                const menuItem = (
                  <StyledMenuItem
                    key={index}
                    $color={color}
                    $interactive={!!option.onClick}
                    $disabled={!!option.disabled}
                    $error={!!option.error}
                    $hasChildren={!!option.below}
                    $isSelected={!!option.isSelected}
                    onClick={(event) => {
                      if (option.onClick && !option.disabled) {
                        option.onClick();
                        handleClose(event);
                      }
                    }}
                    onMouseEnter={option.disabled ? undefined : option.onHover}
                    onMouseLeave={option.onMouseLeave}
                    disableRipple={!option.onClick || option.disabled}
                  >
                    <MenuItemContent>
                      <Together $disabled={option.disabled}>
                        {option.icon_left && (
                          <Icon
                            icon={option.icon_left}
                            color={
                              option.error ? (color === 'black' ? Colors.redLight : Colors.red)
                                : option.disabled ? Colors.grey5
                                : (color === 'black' ? Colors.grey4 : Colors.grey6)
                            }
                            size={14}
                          />
                        )}
                        {option.text}
                        {option.text_sub && (
                          <SubText $color={color} $disabled={option.disabled} $error={option.error}>
                            {option.text_sub}
                          </SubText>
                        )}
                      </Together>
                      <TogetherRight>
                        {option.text_right && (
                          <TextRight $color={color}>
                            {option.text_right}
                          </TextRight>
                        )}
                        {option.rightChildren}
                        {option.icon && (
                          <Icon
                            icon={option.icon}
                            color={
                              option.error ? (color === 'black' ? Colors.redLight : Colors.red)
                              // : option.disabled ? (color === 'black' ? Colors.grey4 : Colors.grey6)
                              : (color === 'black' ? Colors.grey3 : Colors.grey7)
                            }
                            size={14}
                          />
                        )}
                      </TogetherRight>
                    </MenuItemContent>
                    {option.below && (
                      <ChildrenContainer $color={color}>
                        {option.below}
                      </ChildrenContainer>
                    )}
                  </StyledMenuItem>
                );

                if (option.tooltip) {
                  return (
                    <Tooltip key={index} {...option.tooltip}>
                      {menuItem}
                    </Tooltip>
                  );
                }

                return menuItem;
              })}
            </StyledMenuList>
            )}
          </StyledPaper>
        </Grow>
      )}
    </Popper>
  );
};

export default MenuOpen;